
import Head from 'next/head'
import Script from 'next/script'
import Link from 'next/link'
import Image from 'next/image'
import { s3Static } from '../../../utils/constants'
import { getArticleDateTime } from '../../../utils/article-date-time'
import { BlogsList } from '../../../components/blogs/BlogList'
import { blogsMaster } from '../../../utils/sg-blogs-data'
import { getRequestJSON } from '../../../api/index/location-api'
import { getWpNewPostsByCategory, getWpPostsByCategory } from '../../../lib/service'
import { getWpHealthFitness, getWpNewHealthFitness, setWpHealthFitness, setWpNewHealthFitness } from '../../../service/wp-blogs-store'
import { useEffect, useState } from 'react'
import { PersonalTrainerHead } from '@/index/service-category/personal-trainer/PersonalTrainerHead'
import { openModal } from 'service/modal-service'
import { TrainerCustomerReviews } from '@/index/service-category/personal-trainer/TrainerCustomerReviews'
import { ptFemaleFaqs } from 'utils/faq'
import { useRouter } from 'next/router'
import { RequestProvider } from 'context/RequestContext'
import { PersonalTrainerWhyUs } from '@/index/service-category/personal-trainer/PersonalTrainerWhyUs'
import { getTestimonials } from 'api/user/profile-api'
import { getPtTestimonials, setPtTestimonials } from 'service/testimonial-store'
import { FaCheckCircle } from 'react-icons/fa'
import { DarkFaq } from '@/index/service-category/personal-trainer/DarkFaq'
import TrainerValueProposition from '@/index/service-category/personal-trainer/TrainerValueProposition'
import { TopTrainersSection } from '@/index/service-category/personal-trainer/TopTrainersSection'
import { HERO_TRAINER_COUNT } from '@/index/service-category/personal-trainer/HeroTrainerStrip'
import { FemaleTrainingFocus } from '@/index/service-category/personal-trainer/FemaleTrainingFocus'
import { downloadTopServiceProviders } from 'api/service-provider/sp-list'
import { toTrainerCards } from 'utils/trainer-card-projection'
import { buildTrainerItemList } from 'utils/trainer-list-schema'
import { getDistanceMetrics } from 'utils/distance-matrix'
import TrustedPartners from '@/index/service-category/personal-trainer/TrustedPartners'
import { localBusinessNode, buildServiceNode } from 'utils/local-business-schema'

export async function getStaticProps() {
    
    const { modifiedDate, month, year } = getArticleDateTime();
    const fitnessBlogs = blogsMaster['health-fitness'].blogArray;
    const wpPosts = getWpHealthFitness().length > 0 ?  getWpHealthFitness() : await getWpPostsByCategory(6, "health-fitness");
    setWpHealthFitness(wpPosts)
    const wpNewPosts = getWpNewHealthFitness().length > 0 ?  getWpNewHealthFitness() : await getWpNewPostsByCategory(6, "health-fitness");
    setWpNewHealthFitness(wpNewPosts)

    const allRequestCategorySettings = await getRequestJSON("Singapore")
    const testimonials = getPtTestimonials().length > 0 ? getPtTestimonials() : await getTestimonials("Fitness Trainer", 0, 6)
    setPtTestimonials(testimonials)

    const distanceUnit = getDistanceMetrics("Singapore")
    const lat = 1.3521
    const lng = 103.8198

    const requestCategorySettings = allRequestCategorySettings.filter(category => category.requestType === "Fitness Trainer")
    // Competing directories list ~28 trainers. The gender filter is server-side
    // and works correctly, so this only needed more depth.
    const spList =  await downloadTopServiceProviders("Fitness Trainer", 0, 30, "Singapore", lat, lng, 50, distanceUnit, false, "female")

  return {
      props: {
        requestCategorySettings,
        modifiedDate,
        month,
        year,
        fitnessBlogs,
        wpPosts,
        wpNewPosts,
        testimonials,
        spList: toTrainerCards(spList)
      },
      // Next.js will attempt to re-generate the page:
      // - When a request comes in
      // - At most once every 10 seconds
      revalidate: 259200, // In seconds
  }
}

export default function Home(props) {
    const router = useRouter()
    const modifiedDate = props.modifiedDate
    const month = props.month
    const year = props.year
    const fitnessBlogs = props.fitnessBlogs
    const wpPosts = props.wpPosts
    const wpNewPosts = props.wpNewPosts
    const requestCategorySettings = props.requestCategorySettings
    const testimonials = props.testimonials
    const spList = props.spList
    // The hero strip shows the first HERO_TRAINER_COUNT trainers, so the grid
    // below starts after them (as on /sg/personal-trainer). A roster too short
    // to split keeps the whole list in the grid rather than leave it empty.
    const gridList = spList.length > HERO_TRAINER_COUNT ? spList.slice(HERO_TRAINER_COUNT) : spList

    const [ requestForm, setRequestForm ] = useState(false)
    
    const requestType = "Fitness Trainer"
    const [isQuickBook, setIsQuickBook] = useState(false)
    const [isCustomBook, setIsCustomBook] = useState(false)

    const openRequestForm = () => {
        window.scrollTo({ top: 0 });
        openModal(setRequestForm)
    }

    useEffect(() => {
        if(router.query.requestForm === "true"){
            setRequestForm(true)
            openModal(setRequestForm)
        }
    }, [router])     

  return (
    <>
      <Head>
        <title>{`Best Female Personal Trainer in Singapore [${year}]`}</title>
        <meta charSet="utf-8"/>
        <meta httpEquiv="X-UA-Compatible" content="IE=edge"/>
        <meta name="viewport" content="width=device-width, initial-scale=1.0" />
        <meta name="description" content="Female personal trainers understand women specific issues; this could range from painful or abnormal periods, pregnancy, pre &amp; postnatal fitness."/>
        <meta name="keywords" content="House Rental, Insurance Plan, Personal Trainer, Maid Service, Handyman Service, Aircon Service, Savings Plan, Food Catering, Interior Design, Wedding Planner, Event Planner"/>
        <meta name="author" content="StringsSG - Personal Training"/>
        <meta name="google-site-verification" content="JAjj5vAc8Xxrh4idRAchZhYR6rwxWPAk3vxMdx-dwVQ" />
        <meta property="og:locale" content="en_US" />
        <meta property="og:url"   content="https://www.stringssg.com/sg/personal-trainer/personal-trainer-singapore-female" />
        <meta property="og:type"   content="article" />
        <meta property="og:title"    content={`Best Female Personal Trainer in Singapore ${year}`}/>
        <meta property="og:description" content="Female personal trainers understand women specific issues; this could range from painful or abnormal periods, pregnancy, pre &amp; postnatal fitness." />
        <meta property="article:publisher" content="https://www.facebook.com/StringsSG/" />
        <meta property="article:published_time" content="2026-01-06T07:30:46+00:00" />
        <meta property="article:modified_time" content={modifiedDate} />
        <meta property="og:image"  content="https://stringssg-assets.s3-ap-southeast-1.amazonaws.com/static/personal-trainer/female-personal-trainer-training-girl-at-gym.jpeg" />
        <meta property="og:image:width" content="1200" />
        <meta property="og:image:height" content="630" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:image" content="https://stringssg-assets.s3-ap-southeast-1.amazonaws.com/static/personal-trainer/female-personal-trainer-training-girl-at-gym.jpeg" />
        <meta name="twitter:site" content="@stringssg"/> 
        <meta name="twitter:url" content="https://www.stringssg.com/sg/personal-trainer/personal-trainer-singapore-female"/>
        <meta name="twitter:title" content={`Best Female Personal Trainer in Singapore ${year}`}/> 
        <meta name="twitter:description" content="Female personal trainers understand women specific issues; this could range from painful or abnormal periods, pregnancy, pre &amp; postnatal fitness."/>
        <link rel="icon" href="/favicon.ico" />
        <script type="application/ld+json"
          dangerouslySetInnerHTML={{ __html:
            `{"@context":"https://schema.org","@graph":[{"@type":"BreadcrumbList","@id":"https://www.stringssg.com/sg/personal-trainer/personal-trainer-singapore-female/#breadcrumblist","itemListElement":[{"@type":"ListItem","@id":"https://www.stringssg.com/#listItem","position":1,"item":{"@type":"WebPage","@id":"https://www.stringssg.com/","name":"Home","url":"https://www.stringssg.com/"},"nextItem":"https://www.stringssg.com/sg/personal-trainer/#listItem"},{"@type":"ListItem","@id":"https://www.stringssg.com/sg/personal-trainer/#listItem","position":2,"item":{"@type":"WebPage","@id":"https://www.stringssg.com/sg/personal-trainer/","name":"Personal Trainer Singapore","url":"https://www.stringssg.com/sg/personal-trainer/"},"previousItem":"https://www.stringssg.com/#listItem","nextItem":"https://www.stringssg.com/sg/personal-trainer/personal-trainer-singapore-female/#listItem"},{"@type":"ListItem","@id":"https://www.stringssg.com/sg/personal-trainer/personal-trainer-singapore-female/#listItem","position":3,"item":{"@type":"WebPage","@id":"https://www.stringssg.com/sg/personal-trainer/personal-trainer-singapore-female/","name":"Female Personal Trainer Singapore","description":"Female personal trainers understand women specific issues; this could range from painful or abnormal periods, pregnancy, pre and postnatal fitness.","url":"https://www.stringssg.com/sg/personal-trainer/personal-trainer-singapore-female/"},"previousItem":"https://www.stringssg.com/sg/personal-trainer/#listItem"}]},${JSON.stringify(localBusinessNode)},${JSON.stringify(buildServiceNode({ pageUrl: "https://www.stringssg.com/sg/personal-trainer/personal-trainer-singapore-female", name: "Female Personal Trainer Singapore", serviceType: "Personal Training", priceRange: "S$84.00 - S$110.00", image: "https://stringssg-assets.s3-ap-southeast-1.amazonaws.com/static/blog/health+and+fitness/lady-personal-trainer-helping-man.jpg" }))},{"@type":"Organization","@id":"https://www.stringssg.com/#organization","name":"StringsSG Singapore","url":"https://www.stringssg.com/"},{"@type":"WebSite","@id":"https://www.stringssg.com/#website","url":"https://www.stringssg.com/","name":"StringsSG Singapore","inLanguage":"en-SG","publisher":{"@id":"https://www.stringssg.com/#organization"}},{"@type":"VideoObject","@id":"https://www.stringssg.com/sg/personal-trainer/personal-trainer-singapore-female/#video","name":"Best Female Personal Trainer in Singapore ${year}","description":"Watch the different type of personal training offered by StringsSG - group classes, muscle building, kick boxing and more.","thumbnailUrl":["https://stringssg-assets.s3-ap-southeast-1.amazonaws.com/static/fitness-package5.jpg","https://stringssg-assets.s3-ap-southeast-1.amazonaws.com/static/fitness-package6.jpg"],"uploadDate":"2025-01-03T08:00:00+08:00","duration":"PT0M16S","contentUrl":"https://youtu.be/CC4Pe6-I1PY","embedUrl":"https://www.youtube.com/embed/CC4Pe6-I1PY","interactionStatistic":{"@type":"InteractionCounter","interactionType":{"@type":"WatchAction"},"userInteractionCount":5647018}},{"@type":"WebPage","@id":"https://www.stringssg.com/sg/personal-trainer/personal-trainer-singapore-female/#webpage","url":"https://www.stringssg.com/sg/personal-trainer/personal-trainer-singapore-female/","name":"Best Female Personal Trainer in Singapore ${year}","description":"Female personal trainers understand women specific issues; this could range from painful or abnormal periods, pregnancy, pre and postnatal fitness.","inLanguage":"en-SG","isPartOf":{"@id":"https://www.stringssg.com/#website"},"breadcrumb":{"@id":"https://www.stringssg.com/sg/personal-trainer/personal-trainer-singapore-female/#breadcrumblist"},"video":{"@id":"https://www.stringssg.com/sg/personal-trainer/personal-trainer-singapore-female/#video"},"datePublished":"2025-01-06T07:30:46+00:00","dateModified":"${modifiedDate}"}]}`
          }}
        />
        {/* Declares the page as the trainer directory it actually is.
            Skipped when the roster is empty - an ItemList with zero items is
            worse than no ItemList at all. */}
        {spList.length > 0 &&
        <script type="application/ld+json"
          dangerouslySetInnerHTML={{ __html: JSON.stringify(buildTrainerItemList({
            pageUrl: "https://www.stringssg.com/sg/personal-trainer/personal-trainer-singapore-female",
            name: "Female Personal Trainers in Singapore",
            trainers: spList
          })) }}
        />
        }
        <link rel="canonical" href="https://www.stringssg.com/sg/personal-trainer/personal-trainer-singapore-female" />
        </Head>
        <div>
            <RequestProvider>
                <PersonalTrainerHead 
                    heroImage={s3Static + "personal-trainer/female-personal-trainer-training-girl-at-gym.jpeg"} 
                    title={`Female Personal Trainer Singapore (${year})`}
                    heading={`Female Personal Trainer Singapore (${year})`}
                    heroCopy="Certified female trainers from $84/session, at your home, condo gym or a nearby park. Matched to your goals and comfort."
                    setIsQuickBook={setIsQuickBook}
                    openRequestForm={openRequestForm}
                    requestForm={requestForm}
                    setRequestForm={setRequestForm}
                    isQuickBook={isQuickBook}
                    isCustomBook={isCustomBook}
                    requestType={requestType}
                    requestCategorySettings={requestCategorySettings}
                    spList={spList}
                    seeAllHref="#top-trainers-singapore"
                    seeAllLabel="See all top female trainers"
                />
            </RequestProvider>
            <section className="mx-auto py-6 px-2 bg-slate-900 relative">
                {/* Mesh gradient */}
                <div className="absolute top-0 right-0 w-96 h-96 bg-orange-500/15 rounded-full blur-3xl" />
                <div className="absolute bottom-0 left-0 w-80 h-80 bg-amber-400/10 rounded-full blur-3xl" />
                {/* <h2 className='text-2xl md:text-4xl text-center text-white font-extrabold tracking-tight py-5'>
                    <strong>Female Personal Trainer Singapore - {year}</strong>
                </h2> */}
                <div className="max-w-6xl mx-auto grid md:grid-cols-2 gap-10 items-center py-6 relative">                    
                    <div className="text-white/60 space-y-5 leading-8 text-lg">
                        <p>
                            Looking for the best <strong>female personal trainer in Singapore</strong>? Whether it&apos;s weight loss, 
                            toning, pre/post-natal fitness, or strength training — StringsSG provides you with full-time certified female trainers who understand your 
                            goals and needs.
                        </p>
                        <p className="text-white/60 mb-8 leading-relaxed">
                            At StringsSG, personal training is one of the key services we offer through our dedicated team of full-time trainers and experienced 
                            fitness professionals across Singapore. We help you get started based on what works best for you:
                        </p>
                        <ul className="space-y-4">
                            <li className="font-bold text-white/90 flex items-center gap-2">
                                <FaCheckCircle className="text-orange-400" />
                                Your location
                            </li>
                            <li className="font-bold text-white/90 flex items-center gap-2">
                                <FaCheckCircle className="text-orange-400" />
                                Your fitness goals
                            </li>
                            <li className="font-bold text-white/90 flex items-center gap-2">
                                <FaCheckCircle className="text-orange-400" />
                                Your preferred training schedule
                            </li>
                        </ul>
                    </div>
                    {/* Image Section */}
                    <div className="relative h-62.5 lg:h-87.5">
                        {/* Floating badge - Certified */}
                        <div className="absolute top-4 right-4 z-10 bg-white/10 backdrop-blur-md rounded-2xl px-4 py-3 shadow-xl border border-white/20">
                            <div className="flex items-center gap-2">
                                <div className="w-10 h-10 bg-linear-to-br from-green-400 to-emerald-500 rounded-xl flex items-center justify-center">
                                    <FaCheckCircle className="text-white" />
                                </div>
                                <div>
                                    <p className="text-xs text-white/50">Certified</p>
                                    <p className="font-bold text-white/90">Trainers</p>
                                </div>
                            </div>
                        </div>
                        
                        <div className="relative w-full h-full rounded-3xl overflow-hidden shadow-2xl">
                            <Image
                                src={s3Static + "personal-trainer/trainer-with-indian-client-tranparent.png"}
                                fill
                                style={{ objectFit: 'contain', objectPosition: 'top' }}
                                alt="Personal Trainer Singapore - StringsSG"
                                sizes="(max-width: 768px) 100vw, 50vw"
                            />
                        </div>
                    </div>
                </div>
            </section>
            <section className="px-3 md:px-4 py-10 md:py-16 bg-slate-900 relative overflow-hidden">
                {/* Mesh gradient blobs */}
                <div className="absolute top-0 left-1/4 w-96 h-96 bg-orange-500/15 rounded-full blur-3xl" />
                <div className="absolute bottom-0 right-1/4 w-80 h-80 bg-amber-400/10 rounded-full blur-3xl" />
                <div className="absolute top-1/2 right-0 w-64 h-64 bg-rose-500/10 rounded-full blur-3xl" />
                {/* Section Header */}
                <div className="max-w-3xl mx-auto text-center mb-10 relative z-10">
                    <h2 className="text-2xl md:text-3xl font-extrabold text-white tracking-tight mb-4">What Our Female Trainers Cover</h2>
                    <p className="text-white/50 leading-relaxed">Our female personal trainers understand women&apos;s unique fitness needs—from hormonal changes and body composition to pregnancy and postpartum recovery. Every program is designed with your comfort, safety, and goals in mind.</p>
                </div>
                {/* Benefit Cards */}
                <div className="max-w-6xl mx-auto grid md:grid-cols-3 gap-6 relative z-10">
                    {[
                    {
                        icon: "🤰",
                        title: "Pre & Postnatal Fitness",
                        desc: "Safe, effective exercises designed for every trimester and postpartum recovery. Our female trainers guide you through pregnancy-safe workouts, diastasis recti repair, pelvic floor strengthening, and gradual return to fitness after delivery.",
                    },
                    {
                        icon: "🌸",
                        title: "Hormonal Cycle Awareness",
                        desc: "Training intensity adapted to your menstrual cycle phases. Our trainers understand how hormonal fluctuations affect energy, strength, and recovery—adjusting workouts so you train smarter, not just harder, throughout the month.",
                    },
                    {
                        icon: "💃",
                        title: "Body Toning & Sculpting",
                        desc: "Targeted programs for lean muscle definition without bulk. Focus on areas women commonly want to tone—arms, glutes, core, and thighs—using resistance training, Pilates-inspired moves, and functional exercises.",
                    },
                    {
                        icon: "🏠",
                        title: "Private Home-Based Training",
                        desc: "Train in complete comfort and privacy at home. Many women prefer a female trainer for home sessions—no gym intimidation, no commute. We bring all equipment to your doorstep for a fully personalized experience.",
                    },
                    {
                        icon: "🩺",
                        title: "Women's Health Conditions",
                        desc: "Specialized programs for PCOS, endometriosis, menopause symptoms, and osteoporosis. Our female trainers create exercise plans that help manage symptoms, reduce inflammation, and improve overall well-being.",
                    },
                    {
                        icon: "🧘",
                        title: "Stress Relief & Mental Wellness",
                        desc: "Exercise tailored for stress management, anxiety reduction, and better sleep. Our trainers incorporate mindful movement, breathing techniques, and progressive routines that boost both physical fitness and mental health.",
                    },
                    ].map((item, i) => (
                    <div
                        key={i}
                        className="bg-white/5 border-2 border-orange-500 rounded-3xl shadow-md hover:shadow-xl hover:border-blue-300 transform hover:-translate-y-1 transition-all duration-300 p-6"
                    >
                        <div className="flex flex-col space-y-4">
                            <div className="flex items-center space-x-3">
                                <div className="text-4xl">{item.icon}</div>
                                <h3 className="font-bold text-lg text-white">{item.title}</h3>
                            </div>
                            <p className="leading-7 text-sm text-white/60">{item.desc}</p>
                        </div>
                    </div>
                    ))}
                </div>
            </section>
            {/* <TrustedPartners/> */}
            <section id="top-trainers-singapore" className='relative bg-slate-900 overflow-hidden'>
                {/* Mesh gradient blobs */}
                <div className="absolute top-0 left-1/3 w-96 h-96 bg-orange-500/10 rounded-full blur-3xl" />
                <div className="absolute bottom-0 right-1/4 w-80 h-80 bg-amber-400/8 rounded-full blur-3xl" />
                
                <div className='relative z-10 py-10 max-w-7xl mx-auto px-3'>
                    <div className="text-center mb-6">
                        <div className="inline-flex items-center gap-2 bg-white/10 backdrop-blur-sm px-3 py-1.5 rounded-full mb-4">
                            <span className="text-sm">🏆</span>
                            <span className="text-xs font-medium text-white/70">Top Trainers</span>
                        </div>
                        <h2 className='text-2xl md:text-4xl font-extrabold text-white tracking-tight'>
                            Top Female Personal Trainers In Singapore
                        </h2>
                    </div>
                    {/* No grid here: TopTrainersSection owns its own responsive grid. Nesting it
                        inside another one put the whole list in a single cell, so on desktop
                        it rendered a third of the width. */}
                    <div>
                        <TopTrainersSection spList={gridList}/>     
                    </div>                    
                    <div className='text-center pt-5'>
                        <Link href="/sg/personal-trainer/trainer-list/Singapore"
                            prefetch={false} className='px-8 py-4 text-lg text-white bg-linear-to-tr from-orange-600 to to-amber-400 rounded-full'>
                                View all Trainers and Gym
                        </Link>
                    </div> 
                </div>          
            </section>  
            <FemaleTrainingFocus/>
            <PersonalTrainerWhyUs/>
            <TrainerCustomerReviews testimonials={testimonials}/>
            <TrainerValueProposition/>
            <section className="relative py-8 md:py-12 overflow-hidden bg-slate-900">
                <div className="absolute top-0 left-0 w-72 h-72 bg-orange-500/8 rounded-full blur-3xl" />
                <div className='relative z-10 max-w-5xl mx-auto px-4'>
                    <DarkFaq 
                        title="Personal Training FAQs"
                        questionsAnswers={ptFemaleFaqs} 
                    />
                </div>
            </section>
            <BlogsList blogs={fitnessBlogs} wpPosts={wpPosts} wpNewPosts={wpNewPosts} theme="dark"/>
        </div>
        <Script 
          id="google-tagmanager-new"
          src="https://www.googletagmanager.com/gtag/js?id=G-QD1HGGHDYJ"
          strategy="lazyOnload"
          onLoad={() => {              
            window.dataLayer = window.dataLayer || [];
            function gtag(){dataLayer.push(arguments);}
            gtag('js', new Date());
          
            gtag('config', 'G-QD1HGGHDYJ');
          }} 
      />
      <Script id="facebook-tracking" strategy="lazyOnload">
          {`!function(f,b,e,v,n,t,s)
					  {if(f.fbq)return;n=f.fbq=function(){n.callMethod?
					  n.callMethod.apply(n,arguments):n.queue.push(arguments)};
					  if(!f._fbq)f._fbq=n;n.push=n;n.loaded=!0;n.version='2.0';
					  n.queue=[];t=b.createElement(e);t.async=!0;
					  t.src=v;s=b.getElementsByTagName(e)[0];
					  s.parentNode.insertBefore(t,s)}(window, document,'script',
					  'https://connect.facebook.net/en_US/fbevents.js');
					  fbq('init', '128634041116395');
					  fbq('track', 'PageView');`}
        </Script>
    </>
  )
}
